import { ThemeEvents } from '@theme/events';

/**
 * Sidebar mega menu: switches right-hand panels when clicking "Shop by" triggers.
 */
class MegaMenuSidebar {
  /** @type {HTMLElement | null} */
  #root = null;

  /** @type {HTMLElement | null} */
  #panelsContainer = null;

  /** @type {HTMLElement[]} */
  #triggers = [];

  /** @type {HTMLElement[]} */
  #panels = [];

  /** @type {HTMLElement[]} */
  #promoItems = [];

  /** @type {HTMLElement | null} */
  #promoFallback = null;

  /** @type {string | null} */
  #defaultPanel = null;

  /**
   * @param {HTMLElement} root
   */
  constructor(root) {
    this.#root = root;
    this.#panelsContainer = root.querySelector('.mega-menu-sidebar__panels');
    this.#triggers = [...root.querySelectorAll('[data-mega-panel]')];
    this.#panels = [...root.querySelectorAll('[data-mega-panel-content]')];
    this.#promoItems = [...root.querySelectorAll('[data-promo-panel]')];
    this.#promoFallback = root.querySelector('[data-promo-fallback]');
    this.#defaultPanel = this.#panelsContainer?.dataset.defaultPanel ?? null;

    this.#triggers.forEach((trigger) => {
      trigger.addEventListener('click', this.#onTriggerClick);
    });

    this.#showDefaultPanel();
  }

  /** Reset to the first "Shop by" panel (e.g. when the parent mega menu opens). */
  showDefaultPanel() {
    if (this.#defaultPanel) {
      this.#showPanel(this.#defaultPanel);
    }
  }

  #showDefaultPanel = () => {
    this.showDefaultPanel();
  };

  /** @param {MouseEvent} event */
  #onTriggerClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const trigger = /** @type {HTMLElement} */ (event.currentTarget);
    const panelId = trigger.dataset.megaPanel;
    if (!panelId) return;
    this.#showPanel(panelId);
  };

  /**
   * @param {string} panelId
   */
  #showPanel(panelId) {
    const panelExists = this.#panels.some((panel) => panel.dataset.megaPanelContent === panelId);
    if (!panelExists) return;

    this.#triggers.forEach((trigger) => {
      const isActive = trigger.dataset.megaPanel === panelId;
      trigger.classList.toggle('mega-menu-sidebar__nav-trigger--active', isActive);
      trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });

    this.#panels.forEach((panel) => {
      const isActive = panel.dataset.megaPanelContent === panelId;
      panel.hidden = !isActive;
      panel.classList.toggle('mega-menu-sidebar__panel--default', isActive);
    });

    this.#showPromo(panelId);
    this.#recalculateMenuHeight();
  }

  /**
   * Switched panels can be taller/shorter than the one measured when the mega
   * menu opened, so ask the parent header-menu to re-measure and resize the
   * underlay card to the now-visible panel. Double rAF lets layout (and
   * content-visibility) settle before measuring.
   */
  #recalculateMenuHeight() {
    const headerMenu = /** @type {any} */ (this.#root?.closest('header-menu'));
    if (typeof headerMenu?.recalculateActiveSubmenuHeight !== 'function') return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => headerMenu.recalculateActiveSubmenuHeight());
    });
  }

  /**
   * Reveals the promo image that matches the active panel, falling back to the
   * shared promo image when the active item has no image of its own.
   * @param {string} panelId
   */
  #showPromo(panelId) {
    let matched = false;

    this.#promoItems.forEach((item) => {
      const isActive = item.dataset.promoPanel === panelId;
      item.hidden = !isActive;
      if (isActive) matched = true;
    });

    if (this.#promoFallback) {
      this.#promoFallback.hidden = matched;
    }
  }
}

/** @type {WeakMap<HTMLElement, MegaMenuSidebar>} */
const instances = new WeakMap();

/**
 * @param {HTMLElement} root
 */
function initMegaMenuSidebar(root) {
  if (instances.has(root)) return;
  instances.set(root, new MegaMenuSidebar(root));
}

function initAllMegaMenuSidebars() {
  document.querySelectorAll('[data-mega-menu-sidebar]').forEach((root) => {
    if (root instanceof HTMLElement) {
      initMegaMenuSidebar(root);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllMegaMenuSidebars);
} else {
  initAllMegaMenuSidebars();
}

document.addEventListener('shopify:section:load', initAllMegaMenuSidebars);

document.addEventListener(ThemeEvents.megaMenuHover, () => {
  document.querySelectorAll('[data-mega-menu-sidebar]').forEach((root) => {
    if (root instanceof HTMLElement) instances.get(root)?.showDefaultPanel();
  });
});

export { MegaMenuSidebar, initMegaMenuSidebar };
